import type { AdminSchemas } from "@10twenty/api/admin";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { useRole } from "../components/Shell";
import {
  clipSearch,
  csvFileName,
  dailyBars,
  dayLabel,
  listQuery,
  niceMax,
  PLATFORM_LABELS,
  sameFilters,
  share,
  STATUS_LABELS,
  type EarlyAccessDay,
  type EarlyAccessFilters,
  type EarlyAccessPlatform,
  type EarlyAccessStatus,
} from "../earlyAccess";
import { errorText, unwrap } from "../problem";

type Stats = AdminSchemas["EarlyAccessStats"];
type Page = AdminSchemas["EarlyAccessPage"];
type Entry = AdminSchemas["EarlyAccessEntry"];

const RANGES = [30, 90, 365] as const;
const STATUSES: readonly EarlyAccessStatus[] = ["all", "pending", "confirmed"];
const PLATFORMS = Object.keys(PLATFORM_LABELS) as EarlyAccessPlatform[];
const EMPTY: EarlyAccessFilters = { status: "all", platform: "", q: "" };

export function EarlyAccessPage() {
  const role = useRole();
  return (
    <>
      <h1>Early access</h1>
      <StatsSection />
      {role === "analyst" ? (
        <p className="muted">Analysts see the counts only; owners and moderators see the addresses.</p>
      ) : (
        <ListSection />
      )}
    </>
  );
}

function StatsSection() {
  const [days, setDays] = useState<number>(90);
  const stats = useQuery({
    queryKey: ["early-access-stats", days],
    queryFn: async () => unwrap(await api.GET("/early-access/stats", { params: { query: { days } } })),
  });
  if (stats.isPending) return <p>Loading…</p>;
  if (stats.isError) return <p className="error">{errorText(stats.error)}</p>;
  const data = stats.data;
  const accounts = data.with_account.confirmed + data.with_account.pending;
  return (
    <section className="stack">
      <div className="tiles">
        <Tile label="Confirmed" value={data.confirmed} />
        <Tile label="Pending" value={data.pending} note="Link not used yet; deleted when it lapses" />
        <Tile
          label="Created an account"
          value={accounts}
          note={`${data.with_account.confirmed} confirmed (${share(data.with_account.confirmed, data.confirmed)}), ${data.with_account.pending} pending`}
        />
      </div>
      <PlatformSplit stats={data} />
      <div className="section-head">
        <h2>Per day (UTC)</h2>
        <label className="inline">
          Range{" "}
          <select value={days} onChange={(event) => setDays(Number(event.target.value))}>
            {RANGES.map((range) => (
              <option key={range} value={range}>
                Last {range} days
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="muted">Addresses still on the list: removed ones and lapsed pending sign-ups are gone.</p>
      <div className="charts">
        <DailyChart title="Sign-ups" days={data.days} pick={(day) => day.signups} />
        <DailyChart title="Confirmations" days={data.days} pick={(day) => day.confirmations} />
      </div>
      <details>
        <summary>Table view</summary>
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th className="num">Sign-ups</th>
              <th className="num">Confirmations</th>
            </tr>
          </thead>
          <tbody>
            {data.days.toReversed().map((day) => (
              <tr key={day.day}>
                <td>{day.day}</td>
                <td className="num">{day.signups}</td>
                <td className="num">{day.confirmations}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}

function Tile({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="tile">
      <span className="tile-label">{label}</span>
      <span className="tile-value">{value.toLocaleString()}</span>
      {note && <span className="tile-note muted">{note}</span>}
    </div>
  );
}

function PlatformSplit({ stats }: { stats: Stats }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Platform</th>
          <th className="num">Confirmed</th>
          <th>Share of confirmed</th>
          <th className="num">Pending</th>
        </tr>
      </thead>
      <tbody>
        {stats.platforms.map((row) => {
          const part = stats.confirmed > 0 ? (row.confirmed / stats.confirmed) * 100 : 0;
          return (
            <tr key={row.platform}>
              <td>{PLATFORM_LABELS[row.platform]}</td>
              <td className="num">{row.confirmed}</td>
              <td>
                <span className="meter" aria-hidden="true">
                  <span style={{ width: `${part}%` }} />
                </span>{" "}
                {share(row.confirmed, stats.confirmed)}
              </td>
              <td className="num">{row.pending}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const PLOT_WIDTH = 600;
const PLOT_HEIGHT = 120;

function DailyChart({
  title,
  days,
  pick,
}: {
  title: string;
  days: readonly EarlyAccessDay[];
  pick: (day: EarlyAccessDay) => number;
}) {
  const series = days.map((day) => ({ day: day.day, value: pick(day) }));
  const total = series.reduce((sum, day) => sum + day.value, 0);
  const top = niceMax(Math.max(0, ...series.map((day) => day.value)));
  const bars = dailyBars(series, PLOT_WIDTH, PLOT_HEIGHT, top);
  const slot = PLOT_WIDTH / Math.max(1, series.length);
  return (
    <figure className="chart">
      <figcaption>
        {title} <span className="muted">· {total.toLocaleString()} in all</span>
      </figcaption>
      <svg
        viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${title} per day, ${total} in all`}
      >
        <line className="chart-grid" x1={0} x2={PLOT_WIDTH} y1={0.5} y2={0.5} />
        <line className="chart-axis" x1={0} x2={PLOT_WIDTH} y1={PLOT_HEIGHT - 0.5} y2={PLOT_HEIGHT - 0.5} />
        {bars.map((bar) => (
          <rect key={bar.day} className="chart-bar" x={bar.x} y={bar.y} width={bar.width} height={bar.height} />
        ))}
        {/* Full-height hover targets, wider than the bars, carry the tooltips. */}
        {series.map((day, index) => (
          <rect key={day.day} className="chart-hit" x={index * slot} y={0} width={slot} height={PLOT_HEIGHT}>
            <title>{`${dayLabel(day.day)}: ${day.value}`}</title>
          </rect>
        ))}
      </svg>
      <div className="chart-scale muted">
        <span>{days[0] ? dayLabel(days[0].day) : ""}</span>
        <span>max {top}</span>
        <span>{days.at(-1) ? dayLabel(days.at(-1)!.day) : ""}</span>
      </div>
    </figure>
  );
}

function ListSection() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<EarlyAccessFilters>(EMPTY);
  const [filters, setFilters] = useState<EarlyAccessFilters>(EMPTY);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const key = ["early-access", filters] as const;
  // Every first page is an audited view: no refetching on focus or a timer.
  const list = useInfiniteQuery({
    queryKey: key,
    queryFn: async ({ pageParam }) =>
      unwrap(await api.GET("/early-access", { params: { query: listQuery(filters, pageParam) } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page) => last.next_cursor ?? undefined,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const remove = useMutation({
    mutationFn: async (entry: Entry) =>
      unwrap(await api.POST("/early-access/remove", { body: { email: entry.email } })),
    onSuccess: (result, entry) => {
      // Dropped from the loaded pages in place, so the view is not re-read (and re-audited).
      queryClient.setQueriesData<InfiniteData<Page>>({ queryKey: ["early-access"] }, (data) => {
        if (!data) return data;
        const shown = data.pages.some((page) => page.entries.some((row) => row.email === entry.email));
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            entries: page.entries.filter((row) => row.email !== entry.email),
            matched: shown && result.removed ? Math.max(0, page.matched - 1) : page.matched,
          })),
        };
      });
      void queryClient.invalidateQueries({ queryKey: ["early-access-stats"] });
    },
  });

  // "Show" always reads the list afresh: the same filters again reload them,
  // new ones drop every other cached view, so going back never shows old rows.
  function apply(event: FormEvent) {
    event.preventDefault();
    const next = { ...draft, q: draft.q.trim() };
    if (sameFilters(next, filters)) void queryClient.resetQueries({ queryKey: key, exact: true });
    else {
      queryClient.removeQueries({ queryKey: ["early-access"], type: "inactive" });
      setFilters(next);
    }
  }

  function confirmRemove(entry: Entry) {
    if (window.confirm(`Remove ${entry.email} from the early access list? This can't be undone.`)) remove.mutate(entry);
  }

  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const result = await api.GET("/early-access/export", {
        params: { query: filters.platform ? { platform: filters.platform } : {} },
        parseAs: "blob",
      });
      const blob = unwrap(result);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = csvFileName(result.response.headers.get("Content-Disposition"));
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      setDownloadError(errorText(error));
    } finally {
      setDownloading(false);
    }
  }

  const pages = list.data?.pages ?? [];
  const entries = pages.flatMap((page) => page.entries);
  const matched = pages[0]?.matched;
  return (
    <section className="stack">
      <div className="section-head">
        <h2>Addresses</h2>
        <button type="button" className="button-quiet" onClick={() => void download()} disabled={downloading}>
          Download confirmed{filters.platform ? ` (${PLATFORM_LABELS[filters.platform]})` : ""} as CSV
        </button>
      </div>
      <p className="muted">Viewing, searching and downloading are recorded in the audit log.</p>
      {downloadError && <p className="error">{downloadError}</p>}
      <form className="filters" onSubmit={apply}>
        <label>
          Status
          <select
            value={draft.status}
            onChange={(event) => setDraft({ ...draft, status: event.target.value as EarlyAccessStatus })}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Platform
          <select
            value={draft.platform}
            onChange={(event) => setDraft({ ...draft, platform: event.target.value as EarlyAccessPlatform | "" })}
          >
            <option value="">Any</option>
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {PLATFORM_LABELS[platform]}
              </option>
            ))}
          </select>
        </label>
        <label className="grow">
          Search
          <input
            type="search"
            value={draft.q}
            placeholder="Part of an address"
            onChange={(event) => setDraft({ ...draft, q: clipSearch(event.target.value) })}
          />
        </label>
        <button type="submit">Show</button>
      </form>
      {list.isPending && <p>Loading…</p>}
      {list.isError && <p className="error">{errorText(list.error)}</p>}
      {remove.isError && <p className="error">{errorText(remove.error)}</p>}
      {matched !== undefined && (
        <p className="muted">
          {matched.toLocaleString()} {matched === 1 ? "address" : "addresses"}
        </p>
      )}
      {entries.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Platform</th>
              <th>Status</th>
              <th>Signed up</th>
              <th>Confirmed</th>
              <th>Account</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.email}>
                <td>{entry.email}</td>
                <td>{PLATFORM_LABELS[entry.platform]}</td>
                <td>{entry.status === "confirmed" ? "Confirmed" : "Pending"}</td>
                <td>{new Date(entry.created_at).toLocaleString()}</td>
                <td>
                  {entry.confirmed_at ? (
                    new Date(entry.confirmed_at).toLocaleString()
                  ) : entry.link_expires_at ? (
                    <span className="muted">Link lapses {new Date(entry.link_expires_at).toLocaleDateString()}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{entry.has_account ? "Yes" : "—"}</td>
                <td>
                  <button
                    type="button"
                    className="button-quiet"
                    onClick={() => confirmRemove(entry)}
                    disabled={remove.isPending}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.hasNextPage && (
        <button
          type="button"
          className="button-quiet"
          onClick={() => void list.fetchNextPage()}
          disabled={list.isFetchingNextPage}
        >
          {list.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}
